import { useState } from 'react';
import { Link } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import masterBackend from '../../services/masterBackendService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await masterBackend.auth.resetPassword(email.trim());
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: 'Password reset link sent! Please check your email inbox and spam folder.' 
        });
        setEmail('');
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send reset link' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Something went wrong. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black px-4 py-8 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-primary rounded-full mix-blend-screen filter blur-3xl animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-primary-light rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-primary rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/authentication">
            <img
              src="/assets/images/Noonx_logo_small.png"
              alt="NOONX"
              className="h-14 mx-auto mb-4 drop-shadow-lg"
              loading="eager"
              fetchpriority="high"
            />
          </Link>
        </div>

        {/* Card */}
        <div className="bg-dark-800 rounded-2xl shadow-2xl p-8 border border-dark-700 backdrop-blur-xl">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/30">
              <Icon name="Lock" size={36} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">
              Reset Password
            </h1>
            <p className="text-sm text-gray-400 leading-relaxed">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {/* Message */}
          {message && (
            <div className={`p-4 rounded-xl mb-6 ${
              message.type === 'success' 
                ? 'bg-green-900/20 border border-green-500/30' 
                : 'bg-red-900/20 border border-red-500/30'
            }`}>
              <div className="flex items-start gap-3">
                <Icon 
                  name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
                  size={20} 
                  className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}
                />
                <p className={`text-sm font-medium ${
                  message.type === 'success' ? 'text-green-300' : 'text-red-300'
                }`}>
                  {message.text}
                </p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Email Address"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              className="border-dark-600 focus:border-primary focus:ring-primary"
              required
            />

            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isLoading}
              className="bg-primary hover:bg-primary-dark text-white font-semibold py-3 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Send Reset Link
            </Button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/authentication"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-primary transition-all"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Sign In
            </Link>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Need help?{' '}
            <Link to="/help" className="text-primary hover:text-primary-light hover:underline font-medium">
              Contact Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
